'use client';
import React, { useState, useRef, useCallback } from 'react';
import PresetsPanel from '@/components/PresetsPanel';
import { usePresets } from '@/lib/usePresets';

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

type Preset = 'light' | 'medium' | 'strong' | 'custom';

interface PresetConfig {
  label: string;
  threshold: number;
  minSilenceDuration: number;
  description: string;
  color: string;
}

const PRESETS: Record<Preset, PresetConfig> = {
  light: {
    label: 'Light',
    threshold: 0.005,
    minSilenceDuration: 1.0,
    description: 'Removes only long, obvious silences (≥1s)',
    color: 'bg-green-100 text-green-800 border-green-200',
  },
  medium: {
    label: 'Medium',
    threshold: 0.015,
    minSilenceDuration: 0.5,
    description: 'Removes moderate silences (≥0.5s)',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  strong: {
    label: 'Strong',
    threshold: 0.03,
    minSilenceDuration: 0.2,
    description: 'Removes short pauses too (≥0.2s)',
    color: 'bg-red-100 text-red-800 border-red-200',
  },
  custom: {
    label: 'Custom',
    threshold: 0.01,
    minSilenceDuration: 0.5,
    description: 'Set your own values',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
  },
};

const PROGRESS_STEPS = [
  { pct: 10, label: 'Loading audio…' },
  { pct: 30, label: 'Decoding audio…' },
  { pct: 50, label: 'Detecting silence…' },
  { pct: 70, label: 'Rebuilding audio…' },
  { pct: 85, label: 'Encoding output…' },
  { pct: 100, label: 'Done!' },
];

interface SilencePresetSettings {
  preset: Preset;
  threshold: number;
  minSilenceDuration: number;
  outputFormat: 'mp3' | 'wav';
}

export default function RemoveSilenceTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; duration: number } | null>(null);
  const [preset, setPreset] = useState<Preset>('medium');
  const [threshold, setThreshold] = useState(PRESETS.medium.threshold);
  const [minSilenceDuration, setMinSilenceDuration] = useState(PRESETS.medium.minSilenceDuration);
  const [outputFormat, setOutputFormat] = useState<'mp3' | 'wav'>('mp3');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultDuration, setResultDuration] = useState<number>(0);
  const [resultSize, setResultSize] = useState(0);
  const [removedSegments, setRemovedSegments] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { presets: savedPresets, savePreset, deletePreset } = usePresets<SilencePresetSettings>('remove-silence');

  const getCurrentSettings = (): SilencePresetSettings => ({
    preset,
    threshold,
    minSilenceDuration,
    outputFormat,
  });

  const applyPresetSettings = (settings: SilencePresetSettings) => {
    setPreset(settings.preset);
    setThreshold(settings.threshold);
    setMinSilenceDuration(settings.minSilenceDuration);
    setOutputFormat(settings.outputFormat);
    setResultUrl(null);
  };

  const setStep = (idx: number) => {
    setProgress(PROGRESS_STEPS[idx].pct);
    setProgressLabel(PROGRESS_STEPS[idx].label);
  };

  const loadFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const allowed = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'webm', 'flac'];
    if (!allowed.includes(ext)) {
      setError('Unsupported file type. Please upload MP3, WAV, OGG, M4A, AAC, or FLAC.');
      return;
    }
    if (file.size > 150 * 1024 * 1024) {
      setError('File too large. Max 150 MB.');
      return;
    }
    setError(null);
    setResultUrl(null);
    const url = URL.createObjectURL(file);
    // Get duration via audio element
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      setAudioFile({ file, url, duration: audio.duration });
    });
    audio.addEventListener('error', () => {
      setAudioFile({ file, url, duration: 0 });
    });
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) loadFile(f);
  };

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      setThreshold(PRESETS[p].threshold);
      setMinSilenceDuration(PRESETS[p].minSilenceDuration);
    }
    setResultUrl(null);
  };

  const handleProcess = async () => {
    if (!audioFile) return;
    setProcessing(true);
    setProgress(0);
    setError(null);
    setResultUrl(null);

    try {
      setStep(0);
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof window.AudioContext }).webkitAudioContext;
      if (!AudioCtx) {
        setError('Web Audio API is not supported in this browser.');
        setProcessing(false);
        return;
      }

      const arrayBuffer = await audioFile.file.arrayBuffer();
      setStep(1);

      const audioCtx = new AudioCtx();
      let decoded: AudioBuffer;
      try {
        decoded = await audioCtx.decodeAudioData(arrayBuffer);
      } catch {
        await audioCtx.close();
        setError('Could not decode audio. Please try a different file format.');
        setProcessing(false);
        return;
      }
      await audioCtx.close();

      setStep(2);

      const sampleRate = decoded.sampleRate;
      const minSilenceSamples = Math.floor(minSilenceDuration * sampleRate);
      const numChannels = decoded.numberOfChannels;
      const totalSamples = decoded.length;

      // Build mono amplitude array for silence detection
      const monoData = new Float32Array(totalSamples);
      for (let ch = 0; ch < numChannels; ch++) {
        const channelData = decoded.getChannelData(ch);
        for (let i = 0; i < totalSamples; i++) {
          monoData[i] += Math.abs(channelData[i]) / numChannels;
        }
      }

      // Find non-silent segments
      const segments: [number, number][] = [];
      let inSilence = false;
      let segStart = 0;
      let silenceStart = 0;
      let removedCount = 0;

      for (let i = 0; i < totalSamples; i++) {
        if (monoData[i] < threshold) {
          if (!inSilence) {
            inSilence = true;
            silenceStart = i;
          }
        } else {
          if (inSilence) {
            inSilence = false;
            const silenceLen = i - silenceStart;
            if (silenceLen >= minSilenceSamples) {
              if (silenceStart > segStart) {
                segments.push([segStart, silenceStart]);
              }
              segStart = i;
              removedCount++;
            }
          }
        }
      }
      // Handle trailing silence
      if (inSilence) {
        const silenceLen = totalSamples - silenceStart;
        if (silenceLen >= minSilenceSamples) {
          if (silenceStart > segStart) segments.push([segStart, silenceStart]);
          removedCount++;
        } else {
          segments.push([segStart, totalSamples]);
        }
      } else {
        if (segStart < totalSamples) segments.push([segStart, totalSamples]);
      }

      setRemovedSegments(removedCount);

      if (segments.length === 0) {
        setError('No non-silent audio found. Try lowering the silence threshold or using the Light preset.');
        setProcessing(false);
        return;
      }

      setStep(3);

      // Build new AudioBuffer from non-silent segments
      const totalLength = segments.reduce((acc, [s, e]) => acc + (e - s), 0);
      if (totalLength === 0) {
        setError('Processing resulted in empty audio. Try a lower threshold.');
        setProcessing(false);
        return;
      }

      const newBuffer = new AudioBuffer({
        numberOfChannels: numChannels,
        length: totalLength,
        sampleRate,
      });

      for (let ch = 0; ch < numChannels; ch++) {
        const srcData = decoded.getChannelData(ch);
        const dstData = newBuffer.getChannelData(ch);
        let dstOffset = 0;
        for (const [s, e] of segments) {
          dstData.set(srcData.subarray(s, e), dstOffset);
          dstOffset += e - s;
        }
      }

      const newDuration = totalLength / sampleRate;
      setResultDuration(newDuration);

      setStep(4);

      // Encode to WAV (always encode to WAV first, then convert if MP3 requested)
      const wavBlob = audioBufferToWav(newBuffer);

      let finalBlob: Blob;
      let finalMime: string;

      if (outputFormat === 'mp3') {
        // Try ffmpeg.wasm for MP3 encoding
        try {
          finalBlob = await encodeWithFfmpeg(wavBlob, 'mp3');
          finalMime = 'audio/mpeg';
        } catch {
          // Fallback to WAV if ffmpeg fails
          finalBlob = wavBlob;
          finalMime = 'audio/wav';
        }
      } else {
        finalBlob = wavBlob;
        finalMime = 'audio/wav';
      }

      setStep(5);

      const resultObjectUrl = URL.createObjectURL(new Blob([finalBlob], { type: finalMime }));
      setResultUrl(resultObjectUrl);
      setResultSize(finalBlob.size);
    } catch (err) {
      console.error(err);
      setError('Processing failed. Please try a different file or format.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const ext = outputFormat === 'mp3' ? 'mp3' : 'wav';
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `no_silence_${audioFile.file.name.replace(/\.[^.]+$/, '')}.${ext}`;
    a.click();
  };

  const handleReset = () => {
    setAudioFile(null);
    setResultUrl(null);
    setError(null);
    setProgress(0);
    setProgressLabel('');
    setPreset('medium');
    setThreshold(PRESETS.medium.threshold);
    setMinSilenceDuration(PRESETS.medium.minSilenceDuration);
  };

  const timeSaved = audioFile && resultDuration > 0
    ? Math.max(0, audioFile.duration - resultDuration)
    : 0;

  return (
    <div className="space-y-5">
      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload */}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">🔇</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">Supports MP3, WAV, OGG, M4A, AAC · Max 150 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {/* File info */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">🔇</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(audioFile.file.size)} · {formatDuration(audioFile.duration)}</p>
            </div>
            <button onClick={handleReset} className="text-gray-400 hover:text-red-500 p-1 transition-colors" title="Remove file">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Original preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Original Audio</p>
            <audio src={audioFile.url} controls className="w-full h-10" />
          </div>

          {/* Custom Presets Panel */}
          <PresetsPanel
            presets={savedPresets}
            onSave={(name) => savePreset(name, getCurrentSettings())}
            onLoad={applyPresetSettings}
            onDelete={deletePreset}
            accentColor="amber"
          />

          {/* Presets */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-3">Removal Strength</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(Object.keys(PRESETS) as Preset[]).map((p) => (
                <button
                  key={p}
                  onClick={() => applyPreset(p)}
                  className={`rounded-xl border-2 px-3 py-2.5 text-center transition-all ${preset === p ? `${PRESETS[p].color} border-current font-bold shadow-sm` : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                >
                  <span className="block text-sm font-semibold">{PRESETS[p].label}</span>
                  <span className="block text-xs mt-0.5 opacity-75 leading-tight">{PRESETS[p].description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Strong warning */}
          {preset === 'strong' && (
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <svg className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-orange-700 text-sm">
                <span className="font-semibold">Strong silence removal may cut natural pauses.</span> Speech may sound rushed or unnatural. Consider using Medium for spoken content.
              </p>
            </div>
          )}

          {/* Custom controls */}
          {preset === 'custom' && (
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 space-y-4">
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider">Custom Settings</p>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-semibold text-gray-700">Silence Threshold</label>
                  <span className="text-purple-700 font-bold text-xs bg-purple-100 px-2 py-0.5 rounded-lg">{threshold.toFixed(3)}</span>
                </div>
                <input
                  type="range" min={0.001} max={0.1} step={0.001} value={threshold}
                  onChange={(e) => { setThreshold(Number(e.target.value)); setResultUrl(null); }}
                  className="w-full accent-purple-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Very sensitive</span><span>Less sensitive</span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-semibold text-gray-700">Min Silence Duration</label>
                  <span className="text-purple-700 font-bold text-xs bg-purple-100 px-2 py-0.5 rounded-lg">{minSilenceDuration.toFixed(1)}s</span>
                </div>
                <input
                  type="range" min={0.1} max={3} step={0.1} value={minSilenceDuration}
                  onChange={(e) => { setMinSilenceDuration(Number(e.target.value)); setResultUrl(null); }}
                  className="w-full accent-purple-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0.1s (short)</span><span>3s (long)</span>
                </div>
              </div>
            </div>
          )}

          {/* Current settings summary (non-custom) */}
          {preset !== 'custom' && (
            <div className="bg-gray-50 rounded-xl p-3 flex flex-wrap gap-4 text-xs text-gray-600">
              <span>Threshold: <strong className="text-gray-800">{threshold.toFixed(3)}</strong></span>
              <span>Min silence: <strong className="text-gray-800">{minSilenceDuration.toFixed(1)}s</strong></span>
            </div>
          )}

          {/* Output format */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Output Format</p>
            <div className="flex gap-2">
              {(['mp3', 'wav'] as const).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => { setOutputFormat(fmt); setResultUrl(null); }}
                  className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${outputFormat === fmt ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                >
                  {fmt.toUpperCase()}
                  <span className="block text-xs font-normal opacity-70">{fmt === 'mp3' ? 'Smaller size' : 'Lossless'}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Progress */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{progressLabel}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Result */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold text-green-800 text-sm">
                  Silence removed! {removedSegments} segment{removedSegments !== 1 ? 's' : ''} cut · {formatBytes(resultSize)}
                </p>
              </div>

              {/* Duration stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl p-3 text-center border border-green-100">
                  <p className="text-xs text-gray-500 mb-1">Original</p>
                  <p className="font-bold text-gray-800 text-lg">{formatDuration(audioFile.duration)}</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center border border-green-100">
                  <p className="text-xs text-gray-500 mb-1">New Duration</p>
                  <p className="font-bold text-green-700 text-lg">{formatDuration(resultDuration)}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                  <p className="text-xs text-amber-600 mb-1">Time Saved</p>
                  <p className="font-bold text-amber-700 text-lg">{formatDuration(timeSaved)}</p>
                </div>
              </div>

              {/* Processed preview */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Processed Audio</p>
                <audio src={resultUrl} controls className="w-full h-10" />
              </div>

              <button
                onClick={handleDownload}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download {outputFormat.toUpperCase()}
              </button>

              <button
                onClick={() => { setResultUrl(null); setProgress(0); setProgressLabel(''); }}
                className="w-full py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium rounded-xl transition-colors text-sm"
              >
                Process Again with Different Settings
              </button>
            </div>
          )}

          {/* Process button */}
          {!resultUrl && (
            <button
              onClick={handleProcess}
              disabled={processing}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
            >
              {processing ? 'Processing…' : '🔇 Remove Silence'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numChannels * 2;
  const ab = new ArrayBuffer(44 + length);
  const view = new DataView(ab);
  const ws = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i));
  };
  ws(0, 'RIFF'); view.setUint32(4, 36 + length, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true); view.setUint16(34, 16, true); ws(36, 'data');
  view.setUint32(40, length, true);
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const s = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}

async function encodeWithFfmpeg(wavBlob: Blob, format: 'mp3' | 'wav'): Promise<Blob> {
  if (format === 'wav') return wavBlob;
  // Dynamically load ffmpeg.wasm
  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
  const ffmpeg = new FFmpeg();
  const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
  });
  await ffmpeg.writeFile('input.wav', await fetchFile(wavBlob));
  await ffmpeg.exec(['-i', 'input.wav', '-codec:a', 'libmp3lame', '-qscale:a', '2', 'output.mp3']);
  const data = await ffmpeg.readFile('output.mp3');
  return new Blob([data], { type: 'audio/mpeg' });
}
